<template>
  <div>
    <WdsStack v-if="loading" align-start direction-column gap-sm class="tw-w-full">
      <LoadingList :count="2" />
    </WdsStack>

    <WdsStack
      v-else-if="error || hasEmployment"
      direction-column
      class="employment-details-container"
      data-test="employment-details-summary"
    >
      <WdsStack direction-row align-center justify-space-between gap-sm>
        <WdsHeading type="title4">
          {{ $t('Employment details') }}
        </WdsHeading>
        <WdsTooltip top>
          <WdsIcon name="info-filled" class="tw-text-gray-500" :size="16" />
          <template #tooltip-content>
            <WdsStack direction-column gap-sm>
              <div>
                {{
                  $t(
                    "Employment details show information about the worker's employment arrangement with Worksome or an EoR partner.",
                  )
                }}
              </div>
            </WdsStack>
          </template>
        </WdsTooltip>
      </WdsStack>

      <WdsAlert v-if="error" error class="tw-mt-2">
        {{ $t('Failed to load employment details') }}
      </WdsAlert>

      <div v-else class="tw-mt-4" data-test="employment-details-content">
        <div class="tw-grid tw-grid-cols-[200px_1fr] tw-gap-y-4">
          <WdsText secondary>
            {{ $t('Employer') }}
          </WdsText>
          <WdsText data-test="employment-employer-name">
            {{ employerName }}
          </WdsText>

          <WdsText secondary>
            {{ $t('EOR onboarding status') }}
          </WdsText>
          <WdsStack direction-row align-center gap-xs data-test="employment-onboarding-status">
            <WdsLabel
              :success="isOnboarded"
              :warning="!isOnboarded"
              :subtle="true"
              :label="onboardingStatusLabel"
            />
            <WdsText v-if="onboardedDate" secondary>
              {{ onboardedDate }}
            </WdsText>
          </WdsStack>

          <template v-if="hasEmploymentCosts">
            <WdsText secondary>
              {{ $t('Employment cost') }}
            </WdsText>
            <WdsStack direction-column gap-xs data-test="employment-costs">
              <WdsStack
                v-if="ongoingCostDisplay.amount || ongoingCostDisplay.percentage"
                direction-row
                gap-sm
                data-test="employment-cost-ongoing"
              >
                <WdsText secondary>{{ $t('Ongoing') }}</WdsText>
                <WdsStack direction-column gap-xs>
                  <WdsText v-if="ongoingCostDisplay.amount">
                    {{ ongoingCostDisplay.amount }}
                  </WdsText>
                  <WdsText v-if="ongoingCostDisplay.percentage">
                    {{ ongoingCostDisplay.percentage }}
                  </WdsText>
                </WdsStack>
              </WdsStack>
              <WdsStack
                v-if="oneTimeCostDisplay"
                direction-row
                gap-sm
                data-test="employment-cost-one-time"
              >
                <WdsText secondary>{{ $t('One-time') }}</WdsText>
                <WdsText>{{ oneTimeCostDisplay }}</WdsText>
              </WdsStack>
            </WdsStack>
          </template>
        </div>
      </div>
    </WdsStack>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsHeading,
  WdsIcon,
  WdsLabel,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { EmployerRecordStatus, EmploymentCostFrequency, EmploymentCostType } from '@/gql/graphql'

const props = defineProps<{
  hireId: string
}>()

const EmploymentCostFragment = graphql(`
  fragment EmploymentCostFields on EmploymentCost {
    id
    type
    frequency
    amount
    currency
  }
`)

const EmploymentFragment = graphql(`
  fragment HireEmploymentDetailsFields on Employment {
    id
    onboardingStatus
    onboardedAt
    employerOfRecord {
      id
      name
    }
    employmentCosts(first: 10) {
      data {
        ...EmploymentCostFields
      }
    }
  }
`)

const HireFragment = graphql(`
  fragment HireEmploymentSummaryFields on Hire {
    id
    employment {
      ...HireEmploymentDetailsFields
    }
  }
`)

const GET_HIRE_EMPLOYMENT_DETAILS = graphql(`
  query GetHireEmploymentDetails($id: ID!) {
    hire(id: $id) {
      ...HireEmploymentSummaryFields
    }
  }
`)

const { result, loading, error } = useQuery(
  GET_HIRE_EMPLOYMENT_DETAILS,
  () => ({ id: props.hireId }),
  {
    clientId: 'publicApi',
  },
)

const hire = computed(() => useFragment(HireFragment, result.value?.hire))

const employment = computed(() =>
  hire.value?.employment ? useFragment(EmploymentFragment, hire.value.employment) : null,
)

const hasEmployment = computed(() => !!employment.value)

const employerName = computed(() => employment.value?.employerOfRecord?.name ?? $t('Worksome'))

const isOnboarded = computed(
  () => employment.value?.onboardingStatus === EmployerRecordStatus.Complete,
)

const onboardingStatusLabel = computed(() => {
  const status = employment.value?.onboardingStatus
  switch (status) {
    case EmployerRecordStatus.Complete:
      return $t('Onboarded')
    case EmployerRecordStatus.Processing:
      return $t('Processing')
    case EmployerRecordStatus.ChangesRequested:
      return $t('Changes requested')
    case EmployerRecordStatus.Incomplete:
    default:
      return $t('Pending')
  }
})

const onboardedDate = computed(() => {
  if (!isOnboarded.value || !employment.value?.onboardedAt) {
    return null
  }
  const { date } = useDate(employment.value.onboardedAt)
  return date
})

const employmentCosts = computed(() => {
  const costs = employment.value?.employmentCosts?.data ?? []

  return costs.map((costData) => useFragment(EmploymentCostFragment, costData))
})

const hasEmploymentCosts = computed(() => employmentCosts.value.length > 0)

const costCurrency = computed(
  () => employmentCosts.value.find((c) => c.currency)?.currency || null,
)

const ongoingCostDisplay = computed(() => {
  const costs = employmentCosts.value

  if (costs.length === 0) {
    return { amount: null, percentage: null }
  }

  const ongoingFixedCosts = costs.filter(
    (cost) =>
      cost.frequency === EmploymentCostFrequency.Ongoing && cost.type === EmploymentCostType.Fixed,
  )
  const totalFixed = ongoingFixedCosts.reduce((sum, cost) => sum + cost.amount, 0)

  const ongoingPercentageCosts = costs.filter(
    (cost) =>
      cost.frequency === EmploymentCostFrequency.Ongoing &&
      cost.type === EmploymentCostType.Percentage,
  )
  const totalPercentage = ongoingPercentageCosts.reduce((sum, cost) => sum + cost.amount, 0)

  const currency = costCurrency.value || 'Unknown currency'

  return {
    amount: totalFixed > 0 ? `${currency} ${totalFixed.toFixed(2)} / invoice` : null,
    percentage: totalPercentage > 0 ? `${totalPercentage.toFixed(2)} % / invoice` : null,
  }
})

const oneTimeCostDisplay = computed(() => {
  const costs = employmentCosts.value

  if (costs.length === 0) {
    return null
  }

  const oneTimeCosts = costs.filter((cost) => cost.frequency === EmploymentCostFrequency.OneTime)

  if (oneTimeCosts.length > 0) {
    const totalOneTime = oneTimeCosts.reduce((sum, cost) => sum + cost.amount, 0)
    const currency = costCurrency.value || 'Unknown currency'

    return `${currency} ${totalOneTime.toFixed(2)}`
  }

  return null
})
</script>

<style lang="scss" scoped>
.employment-details-container {
  @apply tw-bg-white;
  @apply tw-rounded;
  @apply tw-p-3;
  @apply tw-border-[1px];
  @apply tw-border-gray-200;
}
</style>
